import React from 'react';
import { z } from 'zod';
import { safeUrl } from '../url';
import { styled } from 'styled-components';
import type { BlockDefinition } from '../types';

// Generic card grid — header + a responsive grid of cards (image/icon, title,
// description, optional link). Maps Strapi components.compliance-page
// (ComplianceCards) and is reusable for similar card sections.
export const cardGridSchema = z.object({
  eyebrow: z.string().optional(),
  heading: z.string().optional(),
  description: z.string().optional(),
  cards: z
    .array(
      z.object({
        imageUrl: z.string().optional(),
        title: z.string().min(1),
        description: z.string().optional(),
        linkText: z.string().optional(),
        linkUrl: safeUrl.optional(),
      }),
    )
    .min(1),
});

type CardGridProps = z.infer<typeof cardGridSchema>;

const Section = styled.section`
  max-width: 120rem;
  margin: 0 auto;
  padding: 6rem 1.6rem;

  @media (min-width: 64em) {
    padding: 8rem 2.4rem;
  }
`;

const Header = styled.div`
  max-width: 72rem;
  margin: 0 auto 4rem;
  text-align: center;
`;

const Eyebrow = styled.p`
  margin: 0;
  color: #063091;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.5rem;
  font-weight: 500;
  letter-spacing: 3px;
  text-transform: uppercase;
`;

const Heading = styled.h2`
  margin: 0.8rem 0 0;
  color: #0b1020;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 600;
  font-size: clamp(2.4rem, 3.5vw, 3.6rem);
`;

const Description = styled.p`
  margin: 0.8rem 0 0;
  color: #4e4f52;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.6rem;
  line-height: 1.7;
`;

const Grid = styled.div`
  display: grid;
  gap: 2.4rem;
  grid-template-columns: 1fr;

  @media (min-width: 48em) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (min-width: 64em) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  padding: 2.4rem;
  border: 1px solid #e8efff;
  border-radius: 1.6rem;
  background: #ffffff;
`;

const CardImage = styled.img`
  width: 6rem;
  height: 6rem;
  object-fit: contain;
  margin-bottom: 1.6rem;
`;

const CardTitle = styled.h3`
  margin: 0;
  color: #0b1020;
  font-family: 'DM Sans', sans-serif;
  font-weight: 600;
  font-size: 1.9rem;
`;

const CardDesc = styled.p`
  margin: 0.8rem 0 0;
  color: #4e4f52;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.5rem;
  line-height: 1.6;
`;

const CardLink = styled.a`
  margin-top: 1.6rem;
  color: #063091;
  font-family: 'DM Sans', sans-serif;
  font-weight: 600;
  font-size: 1.4rem;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

function CardGrid({ eyebrow, heading, description, cards }: CardGridProps) {
  return (
    <Section>
      {eyebrow || heading || description ? (
        <Header>
          {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
          {heading ? <Heading>{heading}</Heading> : null}
          {description ? <Description>{description}</Description> : null}
        </Header>
      ) : null}
      <Grid>
        {cards.map((card, i) => (
          <Card key={`${card.title}-${i}`}>
            {card.imageUrl ? <CardImage src={card.imageUrl} alt="" /> : null}
            <CardTitle>{card.title}</CardTitle>
            {card.description ? <CardDesc>{card.description}</CardDesc> : null}
            {card.linkText && card.linkUrl ? <CardLink href={card.linkUrl}>{card.linkText} →</CardLink> : null}
          </Card>
        ))}
      </Grid>
    </Section>
  );
}

export const cardGridBlock: BlockDefinition<CardGridProps> = {
  type: 'cardGrid',
  schema: cardGridSchema,
  Renderer: CardGrid,
};
